import { buttonVariants } from "@patient-watch/ui/button";
import { cn } from "@patient-watch/ui/utils";
import { Skeleton } from "@patient-watch/ui/skeleton";

interface PlanCardProps {
    badge: "free" | "premium";
    title: string;
    description: string;
    children?: React.ReactNode;
    actions?: React.ReactNode;
    isLoading?: boolean;
    className?: string;
}

export const PlanCard = ({
    badge,
    title,
    description,
    children,
    actions,
    isLoading = false,
    className,
}: PlanCardProps) => {
    const badgeConfig = {
        free: {
            bgColor: "bg-indigo-200",
            textColor: "text-indigo-600",
            gradientFrom: "from-indigo-50",
            gradientTo: "to-white",
            borderColor: "border-indigo-200",
        },
        premium: {
            bgColor: "bg-emerald-200",
            textColor: "text-emerald-600",
            gradientFrom: "from-emerald-50/50",
            gradientTo: "to-white",
            borderColor: "border-emerald-200",
        },
    };

    const config = badgeConfig[badge];

    if (isLoading) {
        return (
            <div
                className={cn(
                    "relative flex flex-col bg-gradient-to-br shadow-lg hover:shadow-xl p-6 border-2 rounded-2xl h-fit min-h-[580px] overflow-hidden transition-shadow",
                    `${config.gradientFrom} ${config.gradientTo} ${config.borderColor}`,
                    className
                )}
            >
                <div
                    className={cn(
                        "top-0 right-0 absolute px-4 py-2 rounded-bl-2xl font-semibold text-xs uppercase tracking-wide",
                        config.bgColor,
                        config.textColor
                    )}
                >
                    {badge}
                </div>
                <div className="flex-1 space-y-4">
                    <Skeleton className="mb-2 w-3/4 h-8" />
                    <Skeleton className="w-1/2 h-4" />
                    <Skeleton className="mt-4 w-full h-64" />
                </div>
                <div className="space-y-2 mt-8">
                    <Skeleton className="w-full h-12" />
                    <Skeleton className="w-full h-12" />
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn(
                "relative flex flex-col bg-gradient-to-br shadow-lg hover:shadow-xl p-6 border-2 rounded-2xl h-fit min-h-[580px] overflow-hidden transition-shadow",
                `${config.gradientFrom} ${config.gradientTo} ${config.borderColor}`,
                className
            )}
        >
            <div
                className={cn(
                    "top-0 right-0 absolute px-4 py-2 rounded-bl-2xl font-semibold text-xs uppercase tracking-wide",
                    config.bgColor,
                    config.textColor
                )}
            >
                {badge}
            </div>
            <div className="flex-1">
                <h3 className="mb-2 font-bold text-slate-900 text-2xl">{title}</h3>
                <p className="mb-4 text-slate-600 text-sm">{description}</p>
                {children}
            </div>

            {/* Actions at Bottom */}
            {actions && <div className="mt-8">{actions}</div>}
        </div>
    );
};

// Pre-configured action buttons to match the website design
interface PlanActionsProps {
    signupUrl: string;
    pricingUrl: string;
}

export const PlanActions = ({ signupUrl, pricingUrl }: PlanActionsProps) => {
    return (
        <div className="flex sm:flex-row flex-col gap-2">
            <a
                href={signupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                    buttonVariants({ size: "lg" }),
                    "flex justify-center items-center gap-2 bg-emerald-600 hover:bg-emerald-700 shadow-md hover:shadow-lg rounded-xl w-full font-semibold text-white transition-colors"
                )}
            >
                Sign up free
            </a>
            <a
                href={pricingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                    buttonVariants({ variant: "outline", size: "lg" }),
                    "flex justify-center items-center gap-2 hover:bg-slate-50 border border-slate-300 rounded-lg w-full font-medium text-slate-700 text-md transition-colors"
                )}
            >
                See Pricing
            </a>
        </div>
    );
};
